import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import {
  setLocalAccessToken,
  setLocalRefreshToken,
  clearLocalStorage,
} from "../../../lib/storeManager";

export type UserRole = "admin" | "hr" | "interviewer" | "candidate";

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar: string;
  department?: string;
  profileCompleted?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchRole: (role: UserRole) => void;
  completeProfile: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const mockUsers: Record<string, User> = {
  "admin@hireflow.com": {
    id: "u1",
    name: "Admirra John",
    email: "admin@hireflow.com",
    role: "admin",
    avatar: "AJ",
    department: "HR",
    profileCompleted: true,
  },
  "hr@hireflow.com": {
    id: "u2",
    name: "Priya Kapoor",
    email: "hr@hireflow.com",
    role: "hr",
    avatar: "PK",
    department: "HR",
    profileCompleted: true,
  },
  "interviewer@hireflow.com": {
    id: "u3",
    name: "Rahul Mehta",
    email: "interviewer@hireflow.com",
    role: "interviewer",
    avatar: "RM",
    department: "Engineering",
    profileCompleted: true,
  },
  "candidate@hireflow.com": {
    id: "u4",
    name: "Sneha Patel",
    email: "candidate@hireflow.com",
    role: "candidate",
    avatar: "SP",
    profileCompleted: true,
  },
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  // Initialize user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem("userData");
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        console.error("Failed to parse saved user data:", e);
        localStorage.removeItem("userData");
      }
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await fetch(
        "https://atsapi.universal-software.in/api/auth/login/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-ID": "cf350b14-be4e-487c-b63d-8a67860a8500",
          },
          body: JSON.stringify({
            email: email,
            password: password,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Invalid credentials");
      }

      const data = await response.json();

      // Store token using standard storeManager methods
      if (data.access) {
        setLocalAccessToken(data.access);
      }
      if (data.refresh) {
        setLocalRefreshToken(data.refresh);
      }

      // Map API response to User object
      const userData: User = {
        id: data.user?.id || data.id || `u${Date.now()}`,
        name:
          data.user?.full_name ||
          data.user?.name ||
          data.name ||
          data.user?.username ||
          "User",
        email: data.user?.email || data.email || email,
        role: (data.user?.role || data.role || "admin") as UserRole,
        avatar:
          data.user?.avatar ||
          data.avatar ||
          (data.user?.full_name || email).substring(0, 2).toUpperCase(),
        department: data.user?.department || data.department,
        profileCompleted:
          data.user?.profileCompleted ?? data.profileCompleted ?? true,
      };

      setUser(userData);
      localStorage.setItem("userData", JSON.stringify(userData));
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Login failed. Please try again.");
    }
  }, []);

  const signup = useCallback(
    async (name: string, email: string, _password: string) => {
      if (mockUsers[email.toLowerCase()]) {
        throw new Error("An account with this email already exists");
      }
      const initials = name
        .split(" ")
        .map(n => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
      const newUser: User = {
        id: `u${Date.now()}`,
        name,
        email: email.toLowerCase(),
        role: "candidate",
        avatar: initials,
        profileCompleted: false,
      };
      mockUsers[email.toLowerCase()] = newUser;
      setUser(newUser);
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");

      // Call logout API
      await fetch("https://atsapi.universal-software.in/api/auth/logout/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": "cf350b14-be4e-487c-b63d-8a67860a8500",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      // Always clear local state and redirect, even if API fails
      setUser(null);
      clearLocalStorage();
      window.location.href = "/ats/login";
    }
  }, []);

  const switchRole = useCallback((role: UserRole) => {
    setUser(prev => (prev ? { ...prev, role } : null));
  }, []);

  const completeProfile = useCallback(() => {
    setUser(prev => (prev ? { ...prev, profileCompleted: true } : null));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        signup,
        logout,
        switchRole,
        completeProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
